import React, { FunctionComponent } from "react";
import styled from "styled-components";

import { colours } from "../../../utils";
import { uuid } from "../../../utils";
import RxLoadingScreen from "@/components/ui/display/RxLoadingScreen";
import { ArrowLeft, ArrowRight } from "@/components/Icons";
import { Device } from "@/utils/device";

type StyleProps = {
  centerButton?: boolean;
  colour?: string;
  carouselWidth?: number;
  contentDirection?: string;
  transform?: number;
  transformPercentage?: number;
  transition?: boolean;
  right?: boolean;
};

type StepForwardCarouselProps = {
  children: React.ReactNode[];
  centerButton?: boolean;
  hasLeftButton?: boolean;
};

const StepForwardCarousel: FunctionComponent<StepForwardCarouselProps> = (
  props
) => {
  if (!props.children || props.children.length < 1) return <RxLoadingScreen />;
  const carouselWidth = props.children.length * 100;
  const transformPercentage = 100 / props.children.length;
  const [page, setPage] = React.useState(0);
  const [contentDirection, setContentDirection] = React.useState("flex-start");
  const [transform, setTransform] = React.useState(0);
  const [newSlides, setNewSlides] = React.useState([...props.children]);
  const [transition, setTransition] = React.useState(true);

  const handleArrowClick = (direction) => {
    const newArray = [...newSlides];

    if (direction === "right") {
      if (page === 1) {
        newArray.unshift(newArray.pop());
        setNewSlides(newArray);
      }
      setContentDirection("flex-start");
      setTransform(-transformPercentage);
      setPage(-1);
    }

    if (direction === "left") {
      if (page === -1) {
        newArray.push(newArray.shift());
        setNewSlides(newArray);
      }
      setPage(1);
      setContentDirection("flex-end");
      setTransform(transformPercentage);
    }
  };

  const handleTransitionEnd = (element) => {
    if (element.id !== "slider") return;
    const newArray = [...newSlides];
    if (page === -1) {
      newArray.push(newArray.shift());
    } else if (page === 1) {
      newArray.unshift(newArray.pop());
    }
    setNewSlides(newArray);
    setTransition(false);
    setTransform(0);
    setTimeout(() => {
      setTransition(true);
    });
  };

  return (
    <S.Container
      data-cy="step-forward-carousel"
      centerButton={props.centerButton}
    >
      <S.Carousel contentDirection={contentDirection}>
        <S.Slider
          transform={transform}
          transition={transition}
          carouselWidth={carouselWidth}
          onTransitionEnd={(e) => handleTransitionEnd(e.target)}
          id="slider"
        >
          <S.SliderSection transformPercentage={transformPercentage}>
            {newSlides.map((slide, index) => (
              <S.Slide data-cy={`slide-${index}`} key={uuid()}>
                {slide}
              </S.Slide>
            ))}
          </S.SliderSection>
        </S.Slider>
      </S.Carousel>
      {/* {props.hasLeftButton && ( */}
      <S.Button
        centerButton={props.centerButton}
        tabIndex={0}
        aria-label="previous"
        onClick={() => handleArrowClick("left")}
      >
        <ArrowLeft height="40" width="40" colour={colours.shell} />
      </S.Button>
      {/* )} */}
      <S.Button
        centerButton={props.centerButton}
        tabIndex={0}
        aria-label="next"
        right
        onClick={() => handleArrowClick("right")}
      >
        <ArrowRight height="40" width="40" colour={colours.shell} />
      </S.Button>
    </S.Container>
  );
};

const S = () => {};

S.Container = styled.div<StyleProps>`
  position: relative;
  width: 100%;

  ${(p) =>
    !p.centerButton &&
    `
    height: 100%;
  `}
`;

S.Carousel = styled.div<StyleProps>`
  position: relative;
  width: 100%;
  max-width: 100%;
  height: 100%;
  background: ${colours.shell};
  overflow: hidden;
  display: flex;
  justify-content: ${(p) => p.contentDirection};
`;

S.Slider = styled.div<StyleProps>`
  display: flex;
  height: 100%;
  width: ${(p) => p.carouselWidth}%;
  overflow: hidden;
  flex-shrink: 0;
  transition: ${(p) => (p.transition ? "all 0.5s" : "none")};
  transform: translate(${(p) => p.transform}%);
`;

S.Slide = styled.div`
  height: 100%;
  width: 100%;
`;

S.SliderSection = styled.section<StyleProps>`
  flex-basis: ${(p) => p.transformPercentage}%;
  width: ${(p) => p.transformPercentage}%;
  flex-shrink: 0;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

S.Button = styled.button<StyleProps>`
  position: absolute;
  top: 50%;
  left: 5%;
  bottom: 50%;
  right: auto;
  margin: 0 auto;
  transform: translateY(-50%);
  height: 5rem;
  width: 5rem;
  border: 0.2em solid ${colours.shellGuideline};
  border-radius: 50%;
  cursor: pointer;
  background: linear-gradient(
    ${colours.charcoal},
    ${colours.charcoalMid},
    ${colours.charcoal}
  );
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease-out;

  &:hover,
  &:focus-visible {
    filter: brightness(1.05);
    background: linear-gradient(
      ${colours.charcoal},
      ${colours.charcoal},
      ${colours.charcoal}
    );
    border: 0.4em solid ${colours.shellGuideline};
  }

  &:active {
    transform: scale(0.95) translateY(-50%);
  }

  ${(p) =>
    p.centerButton &&
    `
    top: 60%;
    right: 20%;
    bottom: 0;
    left: 0;
  `}

  ${(p) =>
    p.right &&
    `
    top: 50%;
    right: 5%;
    bottom: 50%;
    left: auto;

    ${
      p.centerButton &&
      `
      top: 60%;
      right: 0;
      bottom: 0;
      left: 20%;
    `
    }
  `}

  @media (min-width: ${Device.tablet}) {
    height: 7rem;
    width: 7rem;
    margin: 0 auto;

    ${(p) =>
      p.centerButton &&
      `
      top: 70%;
      right: 25%;
      bottom: 0;
      left: 0;

      ${
        p.right &&
        `
        top: 70%;
        right: 0;
        bottom: 0;
        left: 25%;
      `
      }
    `}
  }

  @media (min-width: ${Device.desktop}) {
    height: 6rem;
    width: 6rem;
  }
`;

export default StepForwardCarousel;
